import api from './api'
import { fetchAuthSession } from 'aws-amplify/auth'

// Submit a form (uses authenticated endpoint if logged in, public if not)
// payload should be { submissionData, roleAssignments?, sectionAssignments?, accessCode?, captchaToken?, captchaAnswer? }
export const submitForm = async (formId, payload) => {
  // Check if user is authenticated by checking Amplify session
  try {
    const session = await fetchAuthSession()
    const idToken = session.tokens?.idToken

    if (idToken) {
      // Use authenticated endpoint - captures userId
      // Pass payload directly (already has submissionData, roleAssignments, etc.)
      const response = await api.post(`/forms/${formId}/submissions`, payload)
      return response.data
    }
  } catch {
    // User not authenticated - fall through to public endpoint
  }

  // Use public endpoint - anonymous submission
  // Note: This will fail with 403 for private forms - that's expected behavior
  // accessCode in payload will be validated by the backend for protected forms
  const response = await api.post(`/public/forms/${formId}/submit`, payload)
  return response.data
}

// Submit a form using the authenticated endpoint (for when caller knows user is logged in)
export const submitFormAuthenticated = async (formId, payload) => {
  const response = await api.post(`/forms/${formId}/submissions`, payload)
  return response.data
}

// Get all submissions for a form (authenticated, with pagination)
// enrich: 'submitter' to include submitter name/email/tenant info (super/platform admin only)
// status: 'complete' (default) | 'draft' | 'all' — admins can view drafts (#410)
export const getSubmissions = async (formId, { cursor, limit = 20, enrich, sortBy, sortOrder, status } = {}) => {
  const params = new URLSearchParams()
  if (cursor) params.append('cursor', cursor)
  params.append('limit', limit.toString())
  if (enrich) params.append('enrich', enrich)
  if (sortBy) params.append('sortBy', sortBy)
  if (sortOrder) params.append('sortOrder', sortOrder)
  if (status && status !== 'complete') params.append('status', status)

  const response = await api.get(`/forms/${formId}/submissions?${params.toString()}`)
  return response.data
}

// Get a single submission by ID
// Accepts either (formId, submissionId) or just (submissionId) for backwards compatibility
export const getSubmission = async (formIdOrSubmissionId, submissionId) => {
  let url
  if (submissionId) {
    // Called with (formId, submissionId)
    url = `/forms/${formIdOrSubmissionId}/submissions/${submissionId}`
  } else {
    // Called with just (submissionId) - legacy path
    url = `/submissions/${formIdOrSubmissionId}`
  }
  const response = await api.get(url)
  return response.data
}

// Update a submission
// Accepts either (formId, submissionId, data, submittedAt) or (submissionId, data, submittedAt) for backwards compatibility
// `options.isAutoSave` (#516) marks a debounced background save of in-progress
// collaborative section data so the backend skips recording an edit-history
// entry for it (an explicit Save/Complete click still records one).
export const updateSubmission = async (formIdOrSubmissionId, submissionIdOrData, dataOrSubmittedAt, submittedAt, options = {}) => {
  let url, submissionData, timestampParam

  if (typeof submissionIdOrData === 'string') {
    // Called with (formId, submissionId, data, submittedAt)
    url = `/forms/${formIdOrSubmissionId}/submissions/${submissionIdOrData}`
    submissionData = dataOrSubmittedAt
    timestampParam = submittedAt
  } else {
    // Called with (submissionId, data, submittedAt) - legacy path
    url = `/submissions/${formIdOrSubmissionId}`
    submissionData = submissionIdOrData
    timestampParam = dataOrSubmittedAt
  }

  // Use PATCH for /forms/{formId}/submissions/{submissionId} endpoint
  // and PUT for legacy /submissions/{submissionId} endpoint
  const method = url.startsWith('/forms/') ? 'patch' : 'put'
  const response = await api[method](url, {
    submissionData: submissionData,
    ...(options.isAutoSave ? { isAutoSave: true } : {})
  }, {
    params: timestampParam ? { submittedAt: timestampParam } : {}
  })
  return response.data
}

// Delete a submission
export const deleteSubmission = async (formId, submissionId) => {
  const response = await api.delete(`/forms/${formId}/submissions/${submissionId}`)
  return response.data
}

// Bulk delete submissions
export const bulkDeleteSubmissions = async (formId, ids) => {
  const response = await api.post(`/forms/${formId}/submissions/bulk-delete`, { ids })
  return response.data
}

// Upgrade a submission to a newer form version
export const upgradeSubmission = async (formId, submissionId, targetVersion) => {
  const response = await api.post(`/forms/${formId}/submissions/${submissionId}/upgrade`, {
    targetVersion
  })
  return response.data
}

// Get all submissions created by the current user (with pagination)
export const getMySubmissions = async ({ cursor, limit = 50 } = {}) => {
  const params = new URLSearchParams()
  if (cursor) params.append('cursor', cursor)
  params.append('limit', limit.toString())

  const queryString = params.toString()
  const response = await api.get(`/my-submissions${queryString ? '?' + queryString : ''}`)
  return response.data
}

// Start a section in a collaborative submission
export const startSection = async (submissionId, sectionId) => {
  const response = await api.post(`/submissions/${submissionId}/sections/${sectionId}/start`)
  return response.data
}

// Complete a section in a collaborative submission
export const completeSection = async (submissionId, sectionId) => {
  const response = await api.post(`/submissions/${submissionId}/sections/${sectionId}/complete`)
  return response.data
}

// Set / extend / remove the expiry deadline on a collaborative submission's
// workflow run (admin-only). Pass a 'YYYY-MM-DD' string to set/extend, or null
// to remove the deadline. Returns { submissionId, expiresAt, expirySetBy, runExpired }.
export const setSubmissionExpiry = async (submissionId, expiresAt) => {
  const response = await api.put(`/submissions/${submissionId}/expiry`, { expiresAt })
  return response.data
}

// Update ticket status on a submission (super/platform admin only)
export const updateTicketStatus = async (formId, submissionId, ticketStatus) => {
  const response = await api.put(`/forms/${formId}/submissions/${submissionId}/ticket-status`, {
    ticketStatus
  })
  return response.data
}

// Generate a single submission's PDF and return a presigned S3 download URL
// (#386) — the PDF is generated server-side, uploaded to S3, and the browser
// downloads directly from S3, avoiding the API Gateway response size limit.
export const downloadSubmissionPdf = async (formId, submissionId) => {
  const response = await api.get(`/forms/${formId}/submissions/${submissionId}/pdf`)
  const { downloadUrl, filename } = response.data
  return { downloadUrl, filename }
}

// Fetch a single submission's audit trail as CSV (blob response, for browser download)
export const getSubmissionAuditTrailCsv = async (formId, submissionId) => {
  const response = await api.get(
    `/forms/${formId}/submissions/${submissionId}/audit-trail/export?format=csv`,
    { responseType: 'blob' }
  )
  return response
}

// Fetch a single submission's audit trail as PDF (base64-decoded to a Blob).
// Named to mirror getSubmissionAuditTrailCsv — this is a raw fetch, not a
// browser-download trigger; see lib/audit-trail-download.js for that.
export const getSubmissionAuditTrailPdf = async (formId, submissionId) => {
  const response = await api.get(`/forms/${formId}/submissions/${submissionId}/audit-trail/export?format=pdf`)
  const { pdfData, filename } = response.data

  // Decode base64 PDF data to a Blob
  const byteCharacters = atob(pdfData)
  const byteNumbers = new Uint8Array(byteCharacters.length)
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i)
  }
  const blob = new Blob([byteNumbers], { type: 'application/pdf' })

  return { blob, filename }
}

// Fetch the audit trail for ALL submissions on a form as CSV (blob response)
export const getFormAuditTrailCsv = async (formId) => {
  const response = await api.get(`/forms/${formId}/audit-trail/export?format=csv`, { responseType: 'blob' })
  return response
}

// Start a bulk PDF export (returns { exportId })
export const startPdfExport = async (formId, submissionIds) => {
  const response = await api.post(`/forms/${formId}/submissions/export-pdf`, { submissionIds })
  return response.data
}

// Get export status (returns { status, downloadUrl?, error? })
export const getExportStatus = async (formId, exportId) => {
  const response = await api.get(`/forms/${formId}/exports/${exportId}`)
  return response.data
}

// Save a new draft submission (authenticated only)
export const saveDraft = async (formId, submissionData) => {
  const response = await api.post(`/forms/${formId}/submissions`, {
    submissionData,
    isDraft: true
  })
  return response.data
}

// Update an existing draft submission's data
export const updateDraft = async (formId, submissionId, submissionData) => {
  const response = await api.patch(`/forms/${formId}/submissions/${submissionId}`, {
    submissionData
  })
  return response.data
}

// Finalize (submit) a draft submission
export const submitDraft = async (formId, submissionId, submissionData) => {
  const response = await api.patch(`/forms/${formId}/submissions/${submissionId}`, {
    submissionData,
    submitDraft: true
  })
  return response.data
}

// Get the current user's draft for a specific form
export const getMyDraft = async (formId) => {
  const response = await api.get(`/forms/${formId}/submissions?status=draft&mine=true`)
  return response.data
}

// Delete a draft submission
export const deleteDraft = async (formId, submissionId) => {
  const response = await api.delete(`/forms/${formId}/submissions/${submissionId}`)
  return response.data
}

// Cold storage archival (#19) — list archived submissions for a form
export const getArchivedSubmissions = async (formId, { cursor, limit = 20 } = {}) => {
  const params = new URLSearchParams()
  if (cursor) params.append('cursor', cursor)
  params.append('limit', limit.toString())

  const response = await api.get(`/forms/${formId}/archive?${params.toString()}`)
  return response.data
}

// Initiate an S3 Glacier restore for an archived submission (returns { status, tier?, downloadUrl? })
export const restoreArchivedSubmission = async (formId, submissionId, tier) => {
  const response = await api.post(`/forms/${formId}/archive/${submissionId}/restore`, tier ? { tier } : {})
  return response.data
}

// Poll archive restore status (returns { status, downloadUrl? })
export const getArchiveRestoreStatus = async (formId, submissionId) => {
  const response = await api.get(`/forms/${formId}/archive/${submissionId}/restore-status`)
  return response.data
}

// Search submissions across every form in the tenant (#469). Requires tenant
// admin or the manage_forms capability — enforced server-side.
// filters: { formId, submittedBy, status ('complete' | 'draft' | 'all'), submittedAfter, submittedBefore }
export const searchSubmissions = async ({ cursor, limit = 20, sortBy, sortOrder, tenantId, ...filters } = {}) => {
  const params = new URLSearchParams()
  if (cursor) params.append('cursor', cursor)
  params.append('limit', limit.toString())
  if (sortBy) params.append('sortBy', sortBy)
  if (sortOrder) params.append('sortOrder', sortOrder)
  if (tenantId) params.append('tenantId', tenantId)
  if (filters.formId) params.append('formId', filters.formId)
  if (filters.submittedBy) params.append('submittedBy', filters.submittedBy)
  if (filters.status && filters.status !== 'complete') params.append('status', filters.status)
  if (filters.submittedAfter) params.append('submittedAfter', filters.submittedAfter)
  if (filters.submittedBefore) params.append('submittedBefore', filters.submittedBefore)

  const response = await api.get(`/submissions/search?${params.toString()}`)
  return response.data
}

// Get support config (authenticated - returns support form URL)
export const getSupportConfig = async () => {
  const response = await api.get('/support-config')
  return response.data
}
